import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import CustomInput from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButton";
import logo1 from "../../assets/Login/logo1.svg";
import logo2 from "../../assets/Login/logo2.svg";
import LogoImg from "../../assets/Login/Logo2.png"
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import showToast from "../../utils/toastNotifications";
import { useThunk } from "../../hooks/useThunk";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { updatePassword, validateToken } from "../../store";

function SetNewPassword() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [linkVerified, setLinkVerified] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [searchParams, setSearchParam] = useSearchParams();

  const [doVerifyPasswordForgot, isVerifying] = useThunk(validateToken);
  const [doChangePassword, isPasswordChanging] = useThunk(updatePassword);

  const token = searchParams.get("token");
  const id = searchParams.get("id");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      password: "",
      confirmPassword: "",
    },
    {
      password: true,
      confirmPassword: true,
    },
    {
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      confirmPassword: true,
    });

    if (validateForm()) {
      //form submission
      const result = await doChangePassword({ token, id, data: formData });

      if (result.success) {
        navigate("/signin", { replace: true });
        showToast("success", result.response?.message);
      } else {
        showToast("error", result.error?.message);
      }
    }
  };

  useEffect(() => {
    if (!token || !id) {
      setLinkVerified(false);
      setVerifyError(errorMessages.FAILED_RESET_PASSWORD);
      return;
    }
    // check if user has valid link to change the password with a backend call
    doVerifyPasswordForgot({ id, token }).then((result) => {
      if (result?.success) {
        setLinkVerified(true);
      } else {
        setLinkVerified(false);
        setVerifyError(result?.error?.message);
      }
    });
  }, [id, token, doVerifyPasswordForgot]);

  const handleOkayButton = () => {
    navigate("/forgot-password", { replace: true });
  };

  return (
    <>
      {/* <<<=== spinner with loading dialog box while link is verifying ===>>> */}
      {isVerifying && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 p-5">
            <h2 className="text-xl font-bold text-blue-shades font-inter">
              Verifying Link...
            </h2>
            <div className="flex items-center justify-center mt-4">
              <div className="loader border-t-4 border-blue-shades rounded-full w-10 h-10 animate-spin"></div>
            </div>
            <p className="mt-4 text-gray-700 font-inter">
              Please wait while we verify your password reset link.
            </p>
          </div>
        </div>
      )}

      {/* Invalid Link notify Dialog Box */}
      {!linkVerified && !isVerifying && (
        <div>
          <div className="fixed font-inter inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-lg w-96 p-5">
              <h2 className="text-xl font-bold text-primary">Invalid Link</h2>
              <p className="mt-2 text-gray-700">
                {verifyError}. Please request a new Link.
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  className="px-4 py-2 bg-primary text-tertiary rounded hover:bg-blue-shades"
                  onClick={handleOkayButton}
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="main-container min-w-[440px] md:flex md:flex-row  w-full h-screen">
        <div className="left-container md:w-[50%] md:px-[3.875rem] md:py-[8.25rem]  px-[4rem] py-[3rem] flex justify-center items-center ">
          <div className="w-full md:w-[512px] md:flex md:flex-col md:my-[5.2813rem] ">
            <div className="flex flex-col gap-[6px] ">
              <h1 className="text-3xl  md:text-[48px] font-bold text-primary font-nonito_sans leading-[48px] ">
                Set New Password
              </h1>
              <p className="text-[#83899F] text-md font-inter leading-[27px] mt-2">
                Please Enter Your New Password
              </p>
            </div>

            <form className="flex flex-col mt-14">
              <CustomInput
                label="New Password"
                onChange={(e) =>
                  handleInputChange("password", e.target.value)
                }
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                error={touched.password && errors.password}
                onBlur={() => handleBlur("password", formData.password)}
                onFocus={() => clearError("password")}
                outerContainerStyle="mb-[24px]"
                isRequired={true}
                labelStyle="text-black-700 font-inter text-[16px] font-medium leading-[20px] tracking-[-0.15px] "
                className="h-[55px] px-3 rounded-md border border-gray-300 bg-primary-light/30 text-gray-600"
                icon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute border-l-2 pl-3 border-[#CFD9E0] right-4 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? (
                      <AiFillEye size={24} className="text-gray-500" />
                    ) : (
                      <AiFillEyeInvisible size={24} className="text-gray-500" />
                    )}
                  </button>
                }
              />
              <CustomInput
                label="Re-Enter Password "
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                error={touched.confirmPassword && errors.confirmPassword}
                onBlur={() =>
                  handleBlur("confirmPassword", formData.confirmPassword)
                }
                onFocus={() => clearError("confirmPassword")}
                isRequired={true}
                onChange={(e) =>
                  handleInputChange("confirmPassword", e.target.value)
                }
                className="h-[55px] px-3 rounded-md border border-gray-300 bg-primary-light/30 text-gray-600"
                labelStyle="text-black-700 font-inter text-[16px] font-medium leading-[20px] tracking-[-0.15px] "
                icon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute border-l-2 pl-3 border-[#CFD9E0] right-4 top-1/2 -translate-y-1/2"
                  >
                    {showConfirmPassword ? (
                      <AiFillEye size={24} className="text-gray-500" />
                    ) : (
                      <AiFillEyeInvisible size={24} className="text-gray-500" />
                    )}
                  </button>
                }
              />

              <div className="flex flex-col gap-2">
                <CustomButton
                  type="submit"
                  className="h-[60px] mt-[100px] md:mt-[148px] bg-primary font-inter font-semibold text-[20px] text-white rounded-xl text-xl w-full"
                  buttonText="Update Password"
                  onClick={handleSubmit}
                  loading={isPasswordChanging}
                />
              </div>
            </form>
          </div>
        </div>
        <div className="right-container md:w-[50%] bg-login bg-cover  justify-center items-center gap-[0.6875rem] h-full  hidden md:flex ">
          {/* <div>
            <img src={logo1} alt="logo1"></img>
          </div>
          <div>
            <img src={logo2} alt="logo2"></img>
          </div> */}
          <img src={LogoImg} alt="" className="w-[300px] h-auto" />
        </div>
      </div>
    </>
  );
}

export default SetNewPassword;
